import { useCallback, useMemo } from 'react';

import { splitRouteParams } from '@aline/common';
import { useLocation, useNavigate } from 'react-router-dom';

export function getPathWithDrawerSearchParam(
  pathname: string,
  currentSearch: string,
  key: string,
  value: string | null
) {
  return getPathWithDrawerSearchParams(pathname, currentSearch, [{ key, value }]);
}

interface DrawerSearchParamUpdate {
  key: string;
  value: string | null;
}

export function getPathWithDrawerSearchParams(
  pathname: string,
  currentSearch: string,
  updates: DrawerSearchParamUpdate[]
) {
  const searchParams = new URLSearchParams(currentSearch);
  updates.forEach(({ key, value }) => {
    if (value) searchParams.set(key, value);
    else searchParams.delete(key);
  });

  const nextSearch = searchParams.toString();
  /**
   * Inside a tab, `currentSearch` is the tab's durable search merged with its unconsumed one-shot
   * deep-link params (`?citation`, `?highlightRelationship`, …). Those belong to the tab, not to
   * the URL: writing them back makes the browser URL flicker while TabProvider strips them again,
   * and re-delivers the deep link to whoever is still listening.
   */
  return splitRouteParams(`${pathname}${nextSearch ? `?${nextSearch}` : ''}`).durableRoute;
}

/**
 * Navigates only when the drawer params actually change the route. Drawer params are cleared
 * eagerly — every click in the PDF viewer clears `editFieldId` — so without this, routine clicks
 * push a replace navigation per click and churn the location for the whole tree.
 */
function useNavigateToDrawerRoute() {
  const location = useLocation();
  const navigate = useNavigate();

  return useCallback(
    (nextRoute: string, replace: boolean) => {
      const currentRoute = splitRouteParams(`${location.pathname}${location.search}`).durableRoute;
      if (nextRoute === currentRoute) return;

      navigate(nextRoute, { replace });
    },
    [location.pathname, location.search, navigate]
  );
}

export function useDrawerSearchParam(key: string) {
  const location = useLocation();
  const navigateToDrawerRoute = useNavigateToDrawerRoute();
  const value = useMemo(() => {
    return new URLSearchParams(location.search).get(key);
  }, [key, location.search]);
  const setValue = useCallback(
    (nextValue: string | null, options: { pathname?: string; replace?: boolean } = {}) => {
      const pathname = options.pathname ?? location.pathname;
      navigateToDrawerRoute(
        getPathWithDrawerSearchParam(pathname, location.search, key, nextValue),
        options.replace ?? true
      );
    },
    [key, location.pathname, location.search, navigateToDrawerRoute]
  );

  return { setValue, value };
}

export function useSetDrawerSearchParams() {
  const location = useLocation();
  const navigateToDrawerRoute = useNavigateToDrawerRoute();

  return useCallback(
    (
      updates: DrawerSearchParamUpdate[],
      options: { pathname?: string; replace?: boolean } = {}
    ) => {
      const pathname = options.pathname ?? location.pathname;
      navigateToDrawerRoute(
        getPathWithDrawerSearchParams(pathname, location.search, updates),
        options.replace ?? true
      );
    },
    [location.pathname, location.search, navigateToDrawerRoute]
  );
}
